import { useRef, useCallback, useEffect, useState } from 'react'

// ============================================================
// WebSocket message types from server
// ============================================================
export type WSMessageType =
  | 'token'
  | 'tool_call'
  | 'form_field'
  | 'complete'
  | 'error'
  | 'confirm_required'
  | 'citations'
  | 'document_scope_changed'
  | 'document_scope_change_failed'

export type ChatDocumentScope =
  | { mode: 'UNSET'; version?: number }
  | { mode: 'ALL'; source?: string; version?: number }
  | { mode: 'SPACE'; space_id: number; space_name?: string; space_type?: 'GENERAL' | 'ENTITY'; source?: string; version?: number }

export interface DocumentCitationData {
  node_id: number
  name?: string | null
}

export type ConfirmEntity =
  | 'task'
  | 'contact'
  | 'lead'
  | 'appointment'
  | 'tag'
  | 'document'
  | string

export type ConfirmAction = 'delete' | string

export interface WSTokenMessage {
  type: 'token'
  content: string
  chat_id: string
}

export interface WSToolCallMessage {
  type: 'tool_call'
  tool: string
  chat_id: string
}

export interface WSFormFieldMessage {
  type: 'form_field'
  field: string
  prompt: string
  chat_id: string
}

export interface WSCompleteMessage {
  type: 'complete'
  chat_id: string
}

export interface WSCitationsMessage {
  type: 'citations'
  citations: DocumentCitationData[]
  chat_id: string
}

export interface WSErrorMessage {
  type: 'error'
  content: string
  chat_id: string
  /** Optional machine-readable error code (es. confirm_expired, confirm_unauthorized) */
  code?: string
}

export interface MailSendPreview {
  draft_id: string
  version: number
  sender: string
  to: string[]
  cc: string[]
  bcc: string[]
  subject: string
  body_text: string
  attachments: Array<{ name: string; size_bytes: number; sha256: string }>
}

export interface WSConfirmRequiredMessage {
  type: 'confirm_required'
  chat_id: string
  op_id: string
  tool: string
  entity: ConfirmEntity
  action: ConfirmAction
  label: string
  expires_at: number
  preview?: MailSendPreview | null
}

export type WSServerMessage =
  | WSTokenMessage
  | WSToolCallMessage
  | WSFormFieldMessage
  | WSCompleteMessage
  | WSErrorMessage
  | WSConfirmRequiredMessage
  | WSCitationsMessage
  | { type: 'document_scope_changed'; chat_id: string; scope: ChatDocumentScope }
  | { type: 'document_scope_change_failed'; chat_id: string; content: string }

// ============================================================
// Client → Server confirm response
// ============================================================
export interface WSConfirmResponse {
  type: 'confirm_response'
  chat_id: string
  op_id: string
  accept: boolean
}

// ============================================================
// Client → Server message format
// ============================================================
export interface WSClientMessage {
  text: string
  chat_id: string
  agent_id?: string
  /** Model speed profile: "fast" (default) or "slow" */
  model_speed?: 'fast' | 'slow'
  attachments?: Array<{
    attachment_id: string
    original_name: string
    size?: number
  }>
  document_scope?: ChatDocumentScope
}

// ============================================================
// Connection state
// ============================================================
export type WSConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error'

// ============================================================
// Hook callbacks
// ============================================================
export interface UseAgentWebSocketOptions {
  /** Called for each streaming token */
  onToken?: (content: string, chatId: string) => void
  /** Called when a tool is being executed */
  onToolCall?: (tool: string, chatId: string) => void
  /** Called when the AI requests a form field */
  onFormField?: (field: string, prompt: string, chatId: string) => void
  /** Called when the response is complete */
  onComplete?: (chatId: string) => void
  /** Called on error */
  onError?: (content: string, chatId: string) => void
  /** Called when the agent is paused awaiting user confirmation for a destructive tool */
  onConfirmRequired?: (msg: WSConfirmRequiredMessage) => void
  onCitations?: (citations: DocumentCitationData[], chatId: string) => void
  onDocumentScopeChanged?: (scope: ChatDocumentScope, chatId: string) => void
  /** Auto-connect on mount (default: true) */
  autoConnect?: boolean
}

// ============================================================
// Build the WebSocket URL
// ============================================================
function getWebSocketUrl(): string {
  // In production/stage, derive WS URL from the current host
  // In dev, connect to the AI container
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8000/'

  // If there's a dedicated AI WS URL env var, use it
  if (process.env.REACT_APP_AI_WS_URL) {
    return process.env.REACT_APP_AI_WS_URL
  }

  // Derive from base URL: replace http(s) with ws(s) and point to AI port
  try {
    const url = new URL(baseUrl)
    const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:'

    // In Docker dev, AI is on crm_ai:1865, but from browser we use localhost:1865
    // In production, we go through reverse proxy at ai.{domain}
    if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
      return `${wsProtocol}//localhost:1865/ws`
    }

    // For production domains like api.hexisai.eu -> ai.hexisai.eu
    const domain = url.hostname.replace(/^api\./, 'ai.')
    return `${wsProtocol}//${domain}/ws`
  } catch {
    return 'ws://localhost:1865/ws'
  }
}

// ============================================================
// Hook
// ============================================================
export function useAgentWebSocket(options: UseAgentWebSocketOptions = {}) {
  const {
    onToken,
    onToolCall,
    onFormField,
    onComplete,
    onError,
    onConfirmRequired,
    onCitations,
    onDocumentScopeChanged,
    autoConnect = true,
  } = options

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const MAX_RECONNECT_ATTEMPTS = 5
  const RECONNECT_BASE_DELAY = 1000

  const [connectionState, setConnectionState] = useState<WSConnectionState>('disconnected')

  // Store callbacks in refs to avoid re-creating connect/disconnect
  const callbacksRef = useRef(options)
  callbacksRef.current = {
    onToken,
    onToolCall,
    onFormField,
    onComplete,
    onError,
    onConfirmRequired,
    onCitations,
    onDocumentScopeChanged,
  }

  const connect = useCallback(() => {
    // Don't connect if already connected or connecting
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      return
    }

    // Clean up any stale socket still in CLOSING state (e.g. after StrictMode remount)
    if (wsRef.current) {
      wsRef.current.onopen = null
      wsRef.current.onclose = null
      wsRef.current.onerror = null
      wsRef.current.onmessage = null
      wsRef.current = null
    }

    // Reset reconnect counter so a fresh connect always works
    reconnectAttemptsRef.current = 0

    const token = localStorage.getItem('accessToken')
    if (!token) {
      console.warn('[AgentWS] No access token found, skipping connection')
      setConnectionState('error')
      return
    }

    const baseWsUrl = getWebSocketUrl()
    const wsUrl = `${baseWsUrl}?token=${encodeURIComponent(token)}`

    setConnectionState('connecting')

    try {
      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onopen = () => {
        if (wsRef.current !== ws) return // stale socket
        console.log('[AgentWS] Connected')
        setConnectionState('connected')
      }

      ws.onmessage = (event) => {
        if (wsRef.current !== ws) return // stale socket
        try {
          const message: WSServerMessage = JSON.parse(event.data)
          const cbs = callbacksRef.current

          switch (message.type) {
            case 'token':
              cbs.onToken?.(message.content, message.chat_id)
              break
            case 'tool_call':
              cbs.onToolCall?.(message.tool, message.chat_id)
              break
            case 'form_field':
              cbs.onFormField?.(message.field, message.prompt, message.chat_id)
              break
            case 'complete':
              cbs.onComplete?.(message.chat_id)
              break
            case 'error':
              cbs.onError?.(message.content, message.chat_id)
              break
            case 'confirm_required':
              cbs.onConfirmRequired?.(message)
              break
            case 'citations':
              cbs.onCitations?.(message.citations, message.chat_id)
              break
            case 'document_scope_changed':
              cbs.onDocumentScopeChanged?.(message.scope, message.chat_id)
              break
            case 'document_scope_change_failed':
              cbs.onError?.(message.content, message.chat_id)
              break
            default:
              console.warn('[AgentWS] Unknown message type:', message)
          }
        } catch (err) {
          console.error('[AgentWS] Failed to parse message:', err)
        }
      }

      ws.onclose = (event) => {
        if (wsRef.current !== ws) return // stale socket from previous mount
        console.log('[AgentWS] Disconnected', event.code, event.reason)
        wsRef.current = null
        setConnectionState('disconnected')

        // Auto-reconnect with exponential backoff (skip if intentional close)
        if (event.code !== 1000 && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = RECONNECT_BASE_DELAY * Math.pow(2, reconnectAttemptsRef.current)
          reconnectAttemptsRef.current++
          console.log(`[AgentWS] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`)
          reconnectTimeoutRef.current = setTimeout(() => {
            connect()
          }, delay)
        }
      }

      ws.onerror = (event) => {
        if (wsRef.current !== ws) return // stale socket
        console.error('[AgentWS] Error:', event)
        setConnectionState('error')
      }
    } catch (err) {
      console.error('[AgentWS] Failed to create WebSocket:', err)
      setConnectionState('error')
    }
  }, [])

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    if (wsRef.current) {
      // Detach handlers so stale close/error events don't interfere with future connections
      wsRef.current.onopen = null
      wsRef.current.onclose = null
      wsRef.current.onerror = null
      wsRef.current.onmessage = null
      wsRef.current.close(1000, 'Client disconnect')
      wsRef.current = null
    }
    setConnectionState('disconnected')
  }, [])

  const sendMessage = useCallback((message: WSClientMessage) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('[AgentWS] Cannot send message: not connected')
      return false
    }

    try {
      // Tag the payload with type:'message' for the new dispatch protocol.
      // Backend defaults to 'message' when type is missing, but tagging
      // explicitly makes the contract symmetric with confirm_response.
      wsRef.current.send(JSON.stringify({ type: 'message', ...message, mail_send_preview_version: 1 }))
      return true
    } catch (err) {
      console.error('[AgentWS] Failed to send message:', err)
      return false
    }
  }, [])

  const sendConfirmResponse = useCallback(
    (payload: Omit<WSConfirmResponse, 'type'>) => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        console.error('[AgentWS] Cannot send confirm: not connected')
        return false
      }
      try {
        wsRef.current.send(
          JSON.stringify({ type: 'confirm_response', ...payload, mail_send_preview_version: 1 })
        )
        return true
      } catch (err) {
        console.error('[AgentWS] Failed to send confirm_response:', err)
        return false
      }
    },
    []
  )

  const sendDocumentScope = useCallback((chatId: string, scope: ChatDocumentScope) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return false
    wsRef.current.send(JSON.stringify({ type: 'set_document_scope', chat_id: chatId, scope }))
    return true
  }, [])

  // Auto-connect on mount, disconnect on unmount
  useEffect(() => {
    if (autoConnect) {
      connect()
    }
    return () => {
      disconnect()
    }
  }, [autoConnect, connect, disconnect])

  return {
    connectionState,
    connect,
    disconnect,
    sendMessage,
    sendDocumentScope,
    sendConfirmResponse,
    isConnected: connectionState === 'connected',
  }
}
